import React from 'react';
import {
  COMMENT_NOTIFICATION_REPLY_CTA,
  getNotificationDocumentTarget,
  getNotificationTarget,
  type NotificationTarget,
} from '../utils/notification-links';

type NotificationMessageProps = {
  message: string;
  type: string;
  metadata: Record<string, unknown> | null;
  isAdmin?: boolean;
  onOpenTarget: (target: NotificationTarget) => void;
  linkStyle?: React.CSSProperties;
};

function renderLinkedTitle(
  line: string,
  documentTitle: string,
  documentTarget: NotificationTarget | null,
  onOpenTarget: (target: NotificationTarget) => void,
  linkStyle: React.CSSProperties
): React.ReactNode {
  if (!documentTarget || !line.includes(`"${documentTitle}"`)) {
    return line;
  }
  const parts = line.split(`"${documentTitle}"`);
  return (
    <>
      {parts[0]}
      <a
        href={documentTarget.href}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpenTarget(documentTarget);
        }}
        style={linkStyle}
      >
        {documentTitle}
      </a>
      {parts[1]}
    </>
  );
}

export const NotificationMessage: React.FC<NotificationMessageProps> = ({
  message,
  type,
  metadata,
  isAdmin = false,
  onOpenTarget,
  linkStyle = {
    color: '#2563eb',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}) => {
  const target = getNotificationTarget(type, metadata, { isAdmin });
  const documentTarget = getNotificationDocumentTarget(type, metadata);
  const isCommentNotification =
    type === 'comment_created' ||
    type === 'comment_reply_created' ||
    type === 'user_mentioned';

  if (!isCommentNotification || !target) {
    if (!target) {
      return <>{message}</>;
    }
    const documentTitleMatch = message.match(/"([^"]+)"/);
    if (!documentTitleMatch) {
      return <>{message}</>;
    }
    const documentTitle = documentTitleMatch[1];
    const parts = message.split(`"${documentTitle}"`);
    return (
      <>
        {parts[0]}
        <a
          href={target.href}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpenTarget(target);
          }}
          style={linkStyle}
        >
          {documentTitle}
        </a>
        {parts[1]}
      </>
    );
  }

  const commentTarget = target;

  const documentTitleMatch = message.match(/"([^"]+)"/);
  const documentTitle = documentTitleMatch?.[1] ?? null;
  const lines = message.split('\n');
  const elements: React.ReactNode[] = [];
  let blockquoteLines: string[] = [];
  let linkedDocumentTitle = false;
  let key = 0;

  const flushBlockquote = (): void => {
    if (blockquoteLines.length === 0) {
      return;
    }
    elements.push(
      <blockquote
        key={`bq-${key++}`}
        style={{
          margin: '0.5em 0',
          paddingLeft: '0.75em',
          borderLeft: '3px solid var(--border, #d1d5db)',
          color: 'inherit',
          opacity: 0.9,
        }}
      >
        {blockquoteLines.map((line, index) => (
          <React.Fragment key={index}>
            {index > 0 ? <br /> : null}
            {line}
          </React.Fragment>
        ))}
      </blockquote>
    );
    blockquoteLines = [];
  };

  for (const line of lines) {
    if (line.startsWith('> ')) {
      blockquoteLines.push(line.slice(2));
      continue;
    }
    if (line === '>') {
      blockquoteLines.push('');
      continue;
    }
    flushBlockquote();
    if (line.length === 0) {
      elements.push(<br key={`br-${key++}`} />);
      continue;
    }
    if (line === COMMENT_NOTIFICATION_REPLY_CTA) {
      elements.push(
        <a
          key={`reply-${key++}`}
          href={commentTarget.href}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpenTarget(commentTarget);
          }}
          style={linkStyle}
        >
          {COMMENT_NOTIFICATION_REPLY_CTA}
        </a>
      );
      continue;
    }
    if (documentTitle && !linkedDocumentTitle && line.includes(`"${documentTitle}"`)) {
      elements.push(
        <React.Fragment key={`title-${key++}`}>
          {renderLinkedTitle(line, documentTitle, documentTarget, onOpenTarget, linkStyle)}
        </React.Fragment>
      );
      linkedDocumentTitle = true;
      continue;
    }
    elements.push(<React.Fragment key={`line-${key++}`}>{line}</React.Fragment>);
  }
  flushBlockquote();

  return <span style={{ whiteSpace: 'pre-wrap' }}>{elements}</span>;
};
